import {
  type PropsWithChildren,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { Arrow } from '../../icons';
import { AnimateHeight } from '../AnimateHeight';
import { Box } from '../Box';
import { Button } from '../Button';
import { Flex } from '../Flex';

export interface ExpandableRegionProps {
  /** Label for the button to collapse the region. */
  collapseLabel: string;
  /** Label for the button to expand the region. */
  expandLabel: string;
  /** Whether the region should be expanded by default. */
  isExpanded?: boolean;
  /** Callback when the region visibility is toggled. */
  onToggle?: (expanded: boolean) => void;
}

/** Render an expandable region with a toggle button. */
export function ExpandableRegion({
  collapseLabel,
  expandLabel,
  isExpanded: initialExpanded,
  onToggle,
  children,
}: PropsWithChildren<ExpandableRegionProps>) {
  const [expanded, setExpanded] = useState(initialExpanded ?? false);
  const regionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialExpanded !== undefined) {
      setExpanded(initialExpanded);
    }
  }, [initialExpanded]);

  const regionId = useId();

  const handleClick = () => {
    setExpanded(previouslyExpanded => {
      const nowExpanded = !previouslyExpanded;
      onToggle?.(nowExpanded);

      return nowExpanded;
    });
  };

  return (
    <Flex direction="column">
      <AnimateHeight animateIn={expanded} nodeRef={regionRef}>
        <div
          id={regionId}
          ref={regionRef}
          hidden={!expanded}
          role="region"
          style={{ display: 'flow-root' }} // Fix potential issues when the children have collapsable margins, that may prevent the height to be calculated correctly
        >
          {children}
        </div>
      </AnimateHeight>
      <Box mt={{ xs: expanded ? 24 : 12, lg: expanded ? 36 : 24 }}>
        <Button
          aria-controls={regionId}
          aria-expanded={expanded}
          onClick={handleClick}
          variant="plain-text-mini"
          icon={<Arrow arrowDirection={expanded ? 'up' : 'down'} />}
        >
          {expanded ? expandLabel : collapseLabel}
        </Button>
      </Box>
    </Flex>
  );
}
